import { useEffect, useState } from "react";
import api from "./api";

/**
 * Module-level cache + hook to fetch /public/settings exactly once per page load.
 * Multiple components (Logo, Header, Footer, SiteMeta, AdminLayout…) can call
 * useSiteSettings() and share the same in-flight promise / cached response.
 *
 * On module init, we read from SiteLayout's localStorage cache (firat.settings.v1)
 * so that useSiteSettings() returns data immediately on first render — no flash.
 */
let _cache = null;
let _promise = null;
const _subscribers = new Set();

// Seed module cache from localStorage so Logo/FiratLogo render instantly
// (same key and TTL as SiteLayout.jsx: 60 s)
try {
  const raw = typeof localStorage !== "undefined" && localStorage.getItem("firat.settings.v1");
  if (raw) {
    const { data, ts } = JSON.parse(raw);
    if (data && typeof data === "object" && typeof ts === "number" && Date.now() - ts <= 60_000) {
      _cache = data;
    }
  }
} catch { /* quota / SSR / parse error — ignore */ }

const loadSettings = () => {
  if (_promise) return _promise;
  _promise = api.get("/public/settings")
    .then((r) => {
      _cache = r.data || {};
      _subscribers.forEach((fn) => fn(_cache));
      return _cache;
    })
    .catch(() => {
      _cache = {};
      _subscribers.forEach((fn) => fn(_cache));
      return _cache;
    });
  return _promise;
};

export const useSiteSettings = () => {
  const [s, setS] = useState(_cache);
  useEffect(() => {
    if (_cache) { setS(_cache); return; }
    let mounted = true;
    const onUpdate = (data) => { if (mounted) setS(data); };
    _subscribers.add(onUpdate);
    loadSettings();
    return () => { mounted = false; _subscribers.delete(onUpdate); };
  }, []);
  return s || {};
};

/** Force a refresh — useful after admin saves settings */
export const refreshSiteSettings = async () => {
  _promise = null;
  _cache = null;
  return loadSettings();
};
